import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';

import styles from './DeleteUseCaseModal.module.css';

/**
 * The props for `DeleteUseCaseModal`.
 * @param useCase The use case to delete.
 */
export interface DeleteUseCaseModalProps {
    hideModal: () => void;
    onSuccess: () => Promise<void>;
    useCase: UseCase | UseCaseForNonUcrmUser;
    ucrmApi: ClientUcrmApi;
    userHasFullUcrmAccess: boolean;
}

/** Renders a modal for confirming deletion of a use case. Used on the Manage Use Case and Businesses and Use Cases pages. */
export const DeleteUseCaseModal = (props: DeleteUseCaseModalProps) => {
    return (
        <ConfirmationModal
            operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM}
            headerText="Delete Use Case"
            areYouSureText={`Are you sure you want to delete "${props.useCase.name}"?`}
            performOperation={async () => {
                await props.ucrmApi.deleteUseCase(props.useCase.vendor_id, props.useCase.id);
                await props.onSuccess();
                return 'Use case deleted.';
            }}
            hideModal={props.hideModal}
        >
            <ul className={styles.deleteConfirmationList}>
                {/* TODO: Consider changing the wording here if the use case is a draft. It may confuse the user to be told things will be deleted that can't have ever been populated for the use case. */}
                <li>
                    <Text>The in-progress due diligence workflow (inherent risk questionnaire, due diligence questionnaires, due diligence assessments, and final review) for the use case will be deleted.</Text>
                </li>
                <li>
                    <Text>All historical due diligence workflows for the use case will be deleted.</Text>
                </li>
                {props.userHasFullUcrmAccess && (props.useCase as UseCase).assessment_workflow_setup.common_assessment_children.length > 0 && (
                    <>
                        <li>
                            <Text>
                                <b>This use case is sharing its due diligence assessments with other use cases.</b> If you delete it, the following use cases will not have any due diligence assessments until their inherent risk questionnaires are submitted again:
                            </Text>
                        </li>
                        <ul>
                            <Text>
                                {(props.useCase as UseCase).assessment_workflow_setup.common_assessment_children.map((child) => {
                                    return <li key={child.id}>{child.name}</li>;
                                })}
                            </Text>
                        </ul>
                    </>
                )}
            </ul>
        </ConfirmationModal>
    );
};
